import React, { useEffect, useState } from 'react';
import { Modal, Button, Spin } from 'antd';
import { useNavigate } from 'react-router-dom';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useMyContext } from '../../context/MyContext';
import { toast } from 'react-toastify';
import { Viewer } from '@toast-ui/react-editor';
import { api } from '../../config/api';

const AiHsTermsPopup = () => {
    const navigate = useNavigate();
    const { user, updateUserProfile, welcomeTourActive } = useMyContext();

    const [terms, setTerms] = useState(null);   // null = still loading
    const [showModal, setShowModal] = useState(false);
    const [isNew, setIsNew] = useState(false);

    // --------------------------------------
    // Fetch HS Compass terms
    // --------------------------------------
    const loadTerms = async () => {
        try {
            const res = await api.get('/terms-conditions?search=hscompass');
            const { data } = res.data;

            if (!data || !data[0]) {
                toast.error("Unable to load HS Compass Terms.");
                return;
            }

            setTerms(data[0]);
        } catch (err) {
            toast.error("Unable to load HS Compass Terms.");
            console.error(err);
        }
    };

    useEffect(() => {
        loadTerms();
    }, []);

    // --------------------------------------
    // Decide whether to show modal
    // --------------------------------------
    useEffect(() => {
        if (!terms || !user) return;

        const hasAccepted = user.hsCompassTermsAccepted;
        const lastVersion = user.hsCompassTermsVersion;
        const currentVersion = terms.version;

        // User has not accepted before
        if (!hasAccepted) {
            setShowModal(true);
            setIsNew(false);
        } else if (lastVersion !== currentVersion) {
            setShowModal(true);
            setIsNew(true);
        } else {
            setShowModal(false);
        }
    }, [terms, user]);

    // ──────────────────────────────
    // BLOCK POPUP DURING WELCOME TOUR !
    // ──────────────────────────────
    if (welcomeTourActive) {
        return null;
    }

    const handleAccept = async () => {
        if (!user) return;

        try {
            await updateDoc(doc(db, 'users', user.uid), {
                hsCompassTermsAccepted: true,
                hsCompassTermsVersion: terms.version,
            });

            updateUserProfile({
                hsCompassTermsAccepted: true,
                hsCompassTermsVersion: terms.version,
            });

            setShowModal(false);
            setIsNew(false);
        } catch (err) {
            toast.error("Error accepting HS Compass terms");
            console.error(err);
            navigate('/');
        }
    };

    // --------------------------------------
    // Reject logic
    // --------------------------------------
    const handleReject = async () => {
        if (!user) return;

        try {
            await updateDoc(doc(db, 'users', user.uid), {
                hsCompassTermsAccepted: false,
            });

            updateUserProfile({ hsCompassTermsAccepted: false });
        } catch (err) {
            toast.error("Error updating terms acceptance.");
            console.error(err);
        }

        toast.info("You must accept the HS Compass terms to continue.");
        navigate('/');
    };

    // --------------------------------------
    // Render Logic
    // --------------------------------------
    if (!terms) {
        return (
            <div className="w-full flex justify-center mt-10">
                <Spin tip="Loading HS Compass Terms..." />
            </div>
        );
    }

    if (!showModal) return null;

    if (user.role !== 'user') return null

    return (
        <Modal
            open
            centered
            width={900}
            closable={false}
            title={
                <div className="flex flex-col">
                    <h2 className="text-xl font-semibold">
                        {isNew
                            ? "We've Updated Our HS Compass™ Terms & Conditions"
                            : "HS Compass™ — Terms & Conditions"}
                    </h2>

                    {isNew && (
                        <p className="text-gray-600 mt-1">
                            Please review and accept the updated terms.
                        </p>
                    )}
                </div>
            }
            footer={[
                <Button onClick={handleReject}>I Decline</Button>,
                <Button type="primary" onClick={handleAccept}>I Accept</Button>,
            ]}
        >
            <Viewer initialValue={terms.content} />
        </Modal>
    );
};

export default AiHsTermsPopup;
